import React, { useState, useEffect } from 'react';
import { X, Lock, Unlock, MessageCircle, Tag, Share, Eye, Mail } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ProfileSettings } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';

interface ProfileSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSettingsUpdate: (settings: ProfileSettings) => void;
}

export const ProfileSettingsModal: React.FC<ProfileSettingsModalProps> = ({
  isOpen,
  onClose,
  onSettingsUpdate
}) => {
  const { user } = useAuthStore();
  const [settings, setSettings] = useState<ProfileSettings>({
    isPrivate: false,
    allowComments: true,
    allowTags: true,
    allowStorySharing: true,
    showActivityStatus: true,
    allowMessageRequests: true,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      loadSettings();
    }
  }, [isOpen, user]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('is_private, allow_comments, allow_tags, allow_story_sharing, show_activity_status, allow_message_requests')
        .eq('id', user?.id)
        .single();

      if (error) throw error;

      if (data) {
        setSettings({
          isPrivate: data.is_private || false,
          allowComments: data.allow_comments !== false,
          allowTags: data.allow_tags !== false,
          allowStorySharing: data.allow_story_sharing !== false,
          showActivityStatus: data.show_activity_status !== false,
          allowMessageRequests: data.allow_message_requests !== false,
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          is_private: settings.isPrivate,
          allow_comments: settings.allowComments,
          allow_tags: settings.allowTags,
          allow_story_sharing: settings.allowStorySharing,
          show_activity_status: settings.showActivityStatus,
          allow_message_requests: settings.allowMessageRequests,
        })
        .eq('id', user?.id);

      if (error) throw error;

      onSettingsUpdate(settings);
      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: keyof ProfileSettings, value: boolean) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Privacy Settings">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Privacy Settings">
      <div className="space-y-6">
        {/* Account Privacy */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Account Privacy</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                {settings.isPrivate ? (
                  <Lock className="h-5 w-5 text-gray-600 mr-3" />
                ) : (
                  <Unlock className="h-5 w-5 text-gray-600 mr-3" />
                )}
                <div>
                  <p className="font-medium text-gray-900">Private Account</p>
                  <p className="text-sm text-gray-500">
                    {settings.isPrivate 
                      ? 'Only approved followers can see your posts'
                      : 'Anyone can see your posts'
                    }
                  </p>
                </div>
              </div>
              <button
                onClick={() => updateSetting('isPrivate', !settings.isPrivate)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.isPrivate ? 'bg-purple-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.isPrivate ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Interactions */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Interactions</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 border-b border-gray-200">
              <div className="flex items-center">
                <MessageCircle className="h-5 w-5 text-gray-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Allow Comments</p>
                  <p className="text-sm text-gray-500">Let others comment on your posts</p>
                </div>
              </div>
              <button
                onClick={() => updateSetting('allowComments', !settings.allowComments)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.allowComments ? 'bg-purple-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.allowComments ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-3 border-b border-gray-200">
              <div className="flex items-center">
                <Tag className="h-5 w-5 text-gray-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Allow Tags</p>
                  <p className="text-sm text-gray-500">Let others tag you in posts</p>
                </div>
              </div>
              <button
                onClick={() => updateSetting('allowTags', !settings.allowTags)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.allowTags ? 'bg-purple-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.allowTags ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-3 border-b border-gray-200">
              <div className="flex items-center">
                <Share className="h-5 w-5 text-gray-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Allow Story Sharing</p>
                  <p className="text-sm text-gray-500">Let others share your posts to their stories</p>
                </div>
              </div>
              <button
                onClick={() => updateSetting('allowStorySharing', !settings.allowStorySharing)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.allowStorySharing ? 'bg-purple-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.allowStorySharing ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Activity */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Activity</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 border-b border-gray-200">
              <div className="flex items-center">
                <Eye className="h-5 w-5 text-gray-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Show Activity Status</p>
                  <p className="text-sm text-gray-500">Let others see when you're active</p>
                </div>
              </div>
              <button
                onClick={() => updateSetting('showActivityStatus', !settings.showActivityStatus)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.showActivityStatus ? 'bg-purple-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.showActivityStatus ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-3">
              <div className="flex items-center">
                <Mail className="h-5 w-5 text-gray-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Allow Message Requests</p>
                  <p className="text-sm text-gray-500">Let others send you message requests</p>
                </div>
              </div>
              <button
                onClick={() => updateSetting('allowMessageRequests', !settings.allowMessageRequests)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.allowMessageRequests ? 'bg-purple-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.allowMessageRequests ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
            fullWidth
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            fullWidth
            isLoading={saving}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </Modal>
  );
};